import React from "react";
import { Link } from "react-router-dom";
import { Cake, Gift, Sparkles, Heart, MapPin, CheckCircle2, Sun, Stars, ArrowRight, Palette } from "lucide-react";
import SakuraDivider from "@/components/SakuraDivider";
import SakuraPetals from "@/components/SakuraPetals";

const HERO_BG = "https://customer-assets.emergentagent.com/job_sakura-treats-2/artifacts/4ggwaw3z_DSC_3356.jpeg";
const PREMIUM = "https://customer-assets.emergentagent.com/job_sakura-treats-2/artifacts/eocw2xva_DDA66E95-ED38-4D2E-B778-B5073CE3EAE9.png";
const IMG_POPS = "https://customer-assets.emergentagent.com/job_sakura-treats-2/artifacts/3k0vnqck_IMG_7725.png";
const IMG_BOX = "https://customer-assets.emergentagent.com/job_sakura-treats-2/artifacts/t016rr76_D4FB7E02-5CAA-4355-8A0C-D491BB605667.png";
const IMG_SEASONAL = "https://customer-assets.emergentagent.com/job_sakura-treats-2/artifacts/hvrnjliu_8813C840-4AE4-4F8F-8C01-70C995F98F81.png";

const features = [
  { icon: Cake, title: "Custom Cake Pops", desc: "From classic flavors to detailed themed designs, every pop is styled for your moment.", img: IMG_POPS, testid: "feature-cake-pops" },
  { icon: Gift, title: "Party Treat Boxes", desc: "Beautifully curated boxes of cake pops, pretzels, rice crispies & more.", img: IMG_BOX, testid: "feature-treat-boxes" },
  { icon: Sun, title: "Seasonal Specials", desc: "Limited-edition holiday treats — cherry blossom springs, cozy fall, twinkly winter.", img: IMG_SEASONAL, testid: "feature-seasonal" },
];

const steps = [
  { n: "01", icon: Palette, title: "Choose your treats", desc: "Browse cake pops, dipped treats, and dessert boxes." },
  { n: "02", icon: Heart, title: "Submit an inquiry", desc: "Tell me your event, theme, colors, and quantities." },
  { n: "03", icon: CheckCircle2, title: "Confirm details", desc: "I'll send a custom quote and lock in your date." },
  { n: "04", icon: Gift, title: "Pick up or delivery", desc: "Local pickup is preferred; delivery based on availability." },
];

const trust = [
  { icon: Sparkles, text: "Made fresh to order" },
  { icon: Palette, text: "Custom colors & themes" },
  { icon: Heart, text: "Birthdays · Showers · Weddings · Holidays" },
  { icon: MapPin, text: "Local Tampa Bay small business" },
];

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden hero-grad">
        <SakuraPetals count={7} />
        <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-12 pt-10 md:pt-16 pb-20 md:pb-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 animate-fade-up">
            <h1
              className="font-brand whitespace-nowrap text-center text-sakura-500 mx-auto py-4 text-6xl sm:text-7xl md:text-8xl lg:text-[110px]"
              style={{
                lineHeight: 1.2,
                textShadow: "0 2px 18px rgba(232,154,174,0.22)",
              }}
              data-testid="hero-brand"
            >
              Sakura Treats
            </h1>
            <div className="mx-auto mt-4 w-28 h-px gold-hair" aria-hidden="true" />
            <p className="mt-6 text-center text-[11px] sm:text-xs tracking-[0.28em] uppercase text-sakura-soft/80 font-semibold flex items-center justify-center gap-2 flex-wrap" data-testid="hero-location">
              <Sparkles className="h-3 w-3 text-sakura-gold" /> Serving Spring Hill, FL · Pasco County · Tampa Bay Area
            </p>
            <p className="mt-6 text-center font-serif font-light text-lg sm:text-xl lg:text-2xl text-sakura-soft/70 leading-snug tracking-wide" data-testid="hero-subheading">
              Elegant Custom Treats for Life's <span className="text-sakura-500/85 italic">Sweetest</span> Moments.
            </p>
            <p className="mt-6 text-base sm:text-lg text-sakura-ink/90 max-w-xl mx-auto lg:mx-0 text-center lg:text-left">
              Handcrafted cake pops and custom sweets designed to make every celebration feel special.
            </p>
            <div className="mt-8 flex flex-wrap justify-center lg:justify-start gap-3">
              <Link to="/custom-orders" className="btn-primary" data-testid="hero-cta-order">
                <Sparkles className="h-4 w-4" /> Start a Custom Order
              </Link>
              <Link to="/menu" className="btn-secondary" data-testid="hero-cta-menu">
                View Treat Menu <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="mt-10 flex items-center justify-center lg:justify-start gap-5 text-sm text-sakura-soft">
              <div className="flex -space-x-2" aria-hidden="true">
                {[0,1,2].map(i => (
                  <span key={i} className={`h-8 w-8 rounded-full border-2 border-white shadow ${["bg-sakura-300","bg-sakura-gold","bg-sakura-400"][i]}`} />
                ))}
              </div>
              <span>Loved by families & event planners across Tampa Bay.</span>
            </div>
          </div>
          <div className="lg:col-span-6 relative animate-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="relative rounded-[36px] overflow-hidden shadow-sakuraStrong border border-sakura-200">
              <img src={HERO_BG} alt="Sakura Treats cake pops with cherry blossoms" className="w-full h-[440px] md:h-[520px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-tr from-sakura-100/40 via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden md:flex items-center gap-3 bg-white rounded-2xl border border-sakura-200 shadow-soft px-4 py-3">
              <span className="h-10 w-10 grid place-items-center rounded-full bg-sakura-200"><Cake className="h-5 w-5 text-sakura-500" /></span>
              <div>
                <div className="text-xs uppercase tracking-wider text-sakura-500 font-bold">Starting at</div>
                <div className="font-serif text-xl text-sakura-ink">$36 / dozen</div>
              </div>
            </div>
            <div className="absolute -top-5 -right-4 hidden md:flex items-center gap-2 bg-sakura-gold/95 text-white rounded-full px-4 py-2 text-xs font-semibold shadow-sakura">
              <Stars className="h-3.5 w-3.5" /> Made Fresh To Order
            </div>
          </div>
        </div>
      </section>

      {/* WELCOME */}
      <section className="section mx-auto max-w-5xl text-center" data-testid="welcome-section">
        <SakuraDivider />
        <h2 className="font-serif text-3xl sm:text-4xl text-sakura-ink">Welcome to Sakura Treats</h2>
        <p className="mt-5 text-sakura-soft text-base sm:text-lg max-w-3xl mx-auto">
          A small custom treat business creating beautiful, delicious sweets made with love. From whimsical cake pops to party-ready dessert boxes, every order is designed to feel personal, thoughtful, and picture-perfect.
        </p>
      </section>

      {/* FEATURES */}
      <section className="section pt-0" data-testid="features-section">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <div>
              <span className="chip"><Cake className="h-3 w-3" /> What I Make</span>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-sakura-ink">Sweet things, made your way</h2>
            </div>
            <Link to="/menu" className="btn-ghost self-start md:self-auto" data-testid="features-see-menu">See full menu <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f) => (
              <article key={f.title} className="card-soft hover:-translate-y-1 transition" data-testid={f.testid}>
                <div className="relative h-48 overflow-hidden">
                  <img src={f.img} alt={f.title} className="w-full h-full object-cover transition group-hover:scale-105" loading="lazy" />
                  <span className="absolute top-3 left-3 h-9 w-9 grid place-items-center rounded-full bg-white/90 backdrop-blur shadow">
                    <f.icon className="h-4 w-4 text-sakura-500" />
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="font-serif text-xl text-sakura-ink">{f.title}</h3>
                  <p className="mt-2 text-sm text-sakura-soft leading-relaxed">{f.desc}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="section bg-sakura-100/50 relative overflow-hidden" data-testid="how-it-works">
        <div className="mx-auto max-w-7xl">
          <div className="text-center">
            <span className="chip"><Heart className="h-3 w-3" /> How It Works</span>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-sakura-ink">Four sweet steps to your order</h2>
            <p className="mt-3 text-sakura-soft max-w-2xl mx-auto">From first note to first bite — here's how we make it happen together.</p>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5 relative">
            {steps.map((s, i) => (
              <div key={s.n} className="card-soft p-7 relative" data-testid={`step-${i+1}`}>
                <div className="flex items-center justify-between">
                  <span className="font-serif italic text-3xl text-sakura-300">{s.n}</span>
                  <span className="h-11 w-11 grid place-items-center rounded-full bg-sakura-200 text-sakura-500">
                    <s.icon className="h-5 w-5" />
                  </span>
                </div>
                <h3 className="mt-5 font-serif text-xl text-sakura-ink">{s.title}</h3>
                <p className="mt-2 text-sm text-sakura-soft">{s.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Link to="/custom-orders" className="btn-primary" data-testid="how-it-works-cta"><Sparkles className="h-4 w-4" /> Start Your Order</Link>
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="section" data-testid="trust-section">
        <div className="mx-auto max-w-7xl grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <span className="chip"><CheckCircle2 className="h-3 w-3" /> Why Sakura</span>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-sakura-ink">Small batch, big heart</h2>
            <p className="mt-4 text-sakura-soft">I take on a limited number of orders each week so every treat is hand-decorated with the care it deserves.</p>
            <ul className="mt-6 space-y-3">
              {trust.map((t) => (
                <li key={t.text} className="flex items-start gap-3">
                  <span className="mt-0.5 h-8 w-8 grid place-items-center rounded-full bg-sakura-200 text-sakura-500"><t.icon className="h-4 w-4" /></span>
                  <span className="text-sakura-ink font-medium">{t.text}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-7">
            <div className="relative rounded-[32px] overflow-hidden shadow-sakura border border-sakura-200">
              <img src={PREMIUM} alt="Beautifully decorated treat box" className="w-full h-[420px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="section pt-0" data-testid="bottom-cta">
        <div className="mx-auto max-w-5xl card-soft p-10 md:p-14 text-center relative overflow-hidden">
          <SakuraPetals count={4} />
          <h2 className="relative z-10 font-serif text-3xl sm:text-4xl text-sakura-ink">Ready to make your event sweeter?</h2>
          <p className="relative z-10 mt-4 text-sakura-soft max-w-xl mx-auto">Submit a quick inquiry — I'll reply with ideas, a custom quote, and next steps.</p>
          <div className="relative z-10 mt-7 flex flex-wrap justify-center gap-3">
            <Link to="/custom-orders" className="btn-primary" data-testid="bottom-cta-order"><Sparkles className="h-4 w-4" /> Start Inquiry</Link>
            <Link to="/gallery" className="btn-secondary" data-testid="bottom-cta-gallery">Browse Gallery</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
